import api from './api.js'

// Prospects/Leads Service
export const prospectsLeadsService = {
  async getAll(params = {}) {
    const response = await api.get('/prospects-leads', { params })
    return response.data.success ? response.data.data : []
  },

  async getById(id) {
    const response = await api.get(`/prospects-leads/${id}`)
    return response.data.success ? response.data.data : null
  },

  async getByPhase(phase, params = {}) {
    const response = await api.get(`/prospects-leads/phase/${phase}`, { params })
    return response.data.success ? response.data.data : []
  },

  async getPipeline() {
    const response = await api.get('/prospects-leads/pipeline')
    return response.data.success ? response.data.data : {}
  },

  async getStats() {
    const response = await api.get('/prospects-leads/stats')
    return response.data.success ? response.data.data : {}
  },

  async getTopOpportunities(params = {}) {
    const response = await api.get('/prospects-leads/top-opportunities', { params })
    return response.data.success ? response.data.data : []
  },

  async sync() {
    const response = await api.post(
      '/prospects-leads/sync',
      {},
      {
        timeout: 600000 // 10 minutes for sync operations
      }
    )
    return response.data
  },

  async updateNotes(id, notes) {
    const response = await api.patch(`/prospects-leads/${id}/notes`, { notes })
    return response.data
  },

  async deleteAll() {
    const response = await api.delete('/prospects-leads/all')
    return response.data
  },

  async updateDates(id, { start_date, expected_closing_date }) {
    const response = await api.patch(`/prospects-leads/${id}/dates`, {
      start_date,
      expected_closing_date
    })
    return response.data
  },

  async updateChanceToScore(id, chance_to_score) {
    const response = await api.patch(`/prospects-leads/${id}/chance-to-score`, { chance_to_score })
    return response.data
  },

  async syncHistorical() {
    const response = await api.post('/prospects-leads/sync-historical', {}, { timeout: 600000 })
    return response.data
  },

  async syncTimelineHistorical() {
    const response = await api.post(
      '/prospects-leads/sync-timeline-historical',
      {},
      { timeout: 7200000 }
    )
    return response.data
  },

  async syncTimelineProspects() {
    const response = await api.post(
      '/prospects-leads/sync-timeline-prospects',
      {},
      { timeout: 900000 }
    )
    return response.data
  }
}

// Prospect Sourcing Status helpers
export const prospectSourcingStatusService = {
  async update(id, status) {
    const response = await api.patch(`/prospects-leads/${id}/sourcing-status`, { status })
    return response.data
  }
}

// Prospect Preferences Service
export const prospectPreferencesService = {
  async getByProspect(prospectId) {
    const response = await api.get(`/prospects/${prospectId}/preferences`)
    return response.data.success ? response.data.data : []
  },

  async getProposedByEmployee(employeeId) {
    const response = await api.get(`/prospects/proposed-by-employee/${employeeId}`)
    return response.data.success ? response.data.data : []
  },

  async batchGetByProspects(prospectIds) {
    const response = await api.get('/prospects/batch-preferences', {
      params: { prospect_ids: prospectIds.join(',') }
    })
    return response.data.success ? response.data.data : {}
  },

  async create(prospectId, data) {
    const response = await api.post(`/prospects/${prospectId}/preferences`, data)
    return response.data
  },

  async update(prospectId, preferenceId, data) {
    const response = await api.put(`/prospects/${prospectId}/preferences/${preferenceId}`, data)
    return response.data
  },

  async delete(prospectId, preferenceId) {
    const response = await api.delete(`/prospects/${prospectId}/preferences/${preferenceId}`)
    return response.data
  },

  async bulkUpdate(prospectId, preferences, userInfo = {}) {
    const response = await api.put(`/prospects/${prospectId}/preferences`, {
      preferences,
      created_by_name: userInfo.name,
      created_by_email: userInfo.email
    })
    return response.data
  },

  async updatePlSelection(prospectId, preferenceId, data) {
    const response = await api.patch(
      `/prospects/${prospectId}/preferences/${preferenceId}/pl-selection`,
      {
        employee_id: data.employee_id,
        dpw: data.dpw,
        selected_by_name: data.selected_by_name,
        selected_by_email: data.selected_by_email
      }
    )
    return response.data
  },

  async clearPlSelection(prospectId, preferenceId) {
    const response = await api.delete(
      `/prospects/${prospectId}/preferences/${preferenceId}/pl-selection`
    )
    return response.data
  }
}

// Prospect Assignment Service
export const prospectAssignmentService = {
  async getAll(params = {}) {
    const response = await api.get('/prospect-assignments', { params })
    return response.data.success ? response.data.data : []
  },

  async getByProspect(prospectId, { includeReleased = false } = {}) {
    const params = includeReleased ? { include_released: 'true' } : {}
    const response = await api.get(`/prospect-assignments/prospect/${prospectId}`, { params })
    return response.data.success ? response.data.data : []
  },

  async batchGetByProspects(prospectIds, { includeReleased = false } = {}) {
    const params = { prospect_ids: prospectIds.join(',') }
    if (includeReleased) params.include_released = 'true'
    const response = await api.get('/prospect-assignments/batch', { params })
    return response.data.success ? response.data.data : {}
  },

  async getByEmployee(employeeId) {
    const response = await api.get(`/prospect-assignments/employee/${employeeId}`)
    return response.data.success ? response.data.data : []
  },

  async create(data) {
    const response = await api.post('/prospect-assignments', data)
    return response.data
  },

  async update(id, data) {
    const response = await api.put(`/prospect-assignments/${id}`, data)
    return response.data
  },

  async delete(id) {
    const response = await api.delete(`/prospect-assignments/${id}`)
    return response.data
  },

  async deleteByProspectAndEmployee(prospectId, employeeId) {
    const response = await api.delete(
      `/prospect-assignments/prospect/${prospectId}/employee/${employeeId}`
    )
    return response.data
  }
}
